import { DialogContent, DialogTitle, Divider, Drawer, ModalClose } from '@mui/joy';
import classNames from 'classnames';

import { type UseToggle } from '@/shared/hooks/UseToggle';

import tabsMenuContext from './Tabs/menu/TabsMenu.context';

const sizes = {
  lg: 'w-1/2',
  md: 'w-1/3',
  sm: 'w-1/4',
  xl: 'w-3/5',
};

type Props<T> = {
  backdropBehavior?: 'closeOnSingleClick' | 'closeOnDoubleClick' | 'noCloseOnClick';
  hideBackdrop?: boolean;
  className?: string;
  description?: string;
  disableEnforceFocus?: boolean;
  icon?: React.ReactNode;
  onClose?: () => void;
  size?: keyof typeof sizes;
  title: string;
  toggle: UseToggle<T>;
  showClose?: boolean;
};

const SidebarModal = <T,>({
  backdropBehavior = 'closeOnDoubleClick',
  children,
  hideBackdrop,
  className,
  description = '',
  disableEnforceFocus = false,
  icon,
  showClose = false,
  onClose,
  size = 'sm',
  title = 'Form Modal title',
  toggle,
}: React.PropsWithChildren<Props<T>>) => {
  const handleClose = () => {
    onClose?.();
    toggle.hide();
  };

  const onClickBackdrop = () => {
    if (backdropBehavior === 'closeOnSingleClick') {
      handleClose();
    }
  };

  const onDoubleClickBackdrop = () => {
    if (backdropBehavior === 'closeOnDoubleClick') {
      handleClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      disableEnforceFocus={disableEnforceFocus}
      hideBackdrop={hideBackdrop}
      open={toggle.value}
      slotProps={{
        backdrop: {
          className: 'bg-black backdrop-blur-none bg-opacity-60',
          onClick: onClickBackdrop,
          onDoubleClick: onDoubleClickBackdrop,
        },
        content: {
          className: classNames(className, sizes[size]),
        },
      }}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;

        handleClose();
      }}
    >
      <DialogContent className="z-modal p-4">
        <DialogTitle className="pb-4">
          {icon} {title}
          {showClose && <ModalClose onClick={handleClose} />}
        </DialogTitle>
        <Divider className="bg-gray-400" />
        {description && <p className="py-4">{description}</p>}
        {/* not a fan of this approach, but I'm guessing it's easier than hunting down all DataTable instances that can't portal their actions */}
        <tabsMenuContext.Provider value={undefined}>{children}</tabsMenuContext.Provider>
      </DialogContent>
    </Drawer>
  );
};

export default SidebarModal;
